import type * as React from "react";

import { cn } from "./cn";

type PageHeaderProps = Omit<React.ComponentProps<"header">, "title"> & {
  /** The small pill above the title (Eyebrow.astro), with the service dot. */
  eyebrow?: React.ReactNode;
  title: React.ReactNode;
  description?: React.ReactNode;
  /** Page actions, at the end of the title row (under it on a phone). */
  actions?: React.ReactNode;
  /** A way back, e.g. the app's own `<Link>` to the list this page sits under. */
  back?: React.ReactNode;
  /** The title's heading level. */
  level?: 1 | 2;
};

/**
 * A page's title block: optional back link and eyebrow chip, the `h3` type
 * role as the title, a `body` description, actions at the end.
 */
function PageHeader({
  eyebrow,
  title,
  description,
  actions,
  back,
  level = 1,
  className,
  ...props
}: PageHeaderProps) {
  const Heading = level === 1 ? "h1" : "h2";
  return (
    <header
      data-slot="page-header"
      className={cn(
        "flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between",
        className,
      )}
      {...props}
    >
      <div className="flex min-w-0 flex-col items-start gap-2">
        {back ? (
          <div
            data-slot="page-header-back"
            className="text-sm font-medium text-muted-foreground [&_a]:rounded-sm [&_a]:outline-none [&_a]:transition-colors [&_a:hover]:text-foreground [&_a:focus-visible]:outline-2 [&_a:focus-visible]:outline-solid [&_a:focus-visible]:outline-offset-2 [&_a:focus-visible]:outline-ring"
          >
            {back}
          </div>
        ) : null}
        {eyebrow ? (
          <span
            data-slot="page-header-eyebrow"
            className="inline-flex items-center gap-2 rounded-pill bg-card px-3 py-1 text-xs leading-[1.4] font-medium text-body shadow-card"
          >
            <span
              aria-hidden="true"
              className="size-1.5 shrink-0 rounded-full bg-service"
            />
            {eyebrow}
          </span>
        ) : null}
        <Heading
          data-slot="page-header-title"
          className="type-h3 text-foreground"
        >
          {title}
        </Heading>
        {description ? (
          <div
            data-slot="page-header-description"
            className="max-w-2xl type-body text-body"
          >
            {description}
          </div>
        ) : null}
      </div>
      {actions ? (
        <div
          data-slot="page-header-actions"
          className="flex flex-wrap items-center gap-2 sm:shrink-0"
        >
          {actions}
        </div>
      ) : null}
    </header>
  );
}

export { PageHeader };
export type { PageHeaderProps };
